/**
 * CL-STORAGE-001A — Fail-safe overall-package publication.
 *
 * Storage upload and Firestore writes are not cross-service atomic.
 * Sequence: allocate fileId (unpublished) → upload → verify metadata →
 * one Firestore batch (record + role + demotes + requested shares).
 * Failures must not publish a visible package. Orphans are reported only.
 */

import {
  ADMIN_UPLOAD_FAILURE_COPY,
  classifyStorageSdkError,
  STORAGE_ERROR_CODES,
} from "./projectFileStorageErrorModel";
import {
  buildSecureDrawingStorageWriteFields,
  buildVersionedDrawingOriginalPath,
  resolveProjectFileSecureObjectPath,
} from "./projectFileSecureStorageModel";
import { buildStorageAdminAuthUids } from "./storageViewAuthProjection";

export const STORAGE_PUBLICATION_STATES = Object.freeze({
  PENDING: "pending",
  READY: "ready",
  FAILED: "failed",
});

function safeStr(value) {
  return String(value ?? "").trim();
}

function fileIdOf(value) {
  if (value && typeof value === "object") return safeStr(value.id || value.fileId);
  return safeStr(value);
}

export function reuseOrAllocatePublicationFileId(pendingFileId, allocateId) {
  const existing = safeStr(pendingFileId);
  if (existing) return existing;
  if (typeof allocateId !== "function") return "";
  return safeStr(allocateId());
}

export function storageSchemaVersionOf(file = {}) {
  const n = Number(file?.storageSchemaVersion);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function isLegacyOrUnversionedStorageSchema(file = {}) {
  return storageSchemaVersionOf(file) < 2;
}

export function normalizeStoragePublicationState(file = {}) {
  return safeStr(file?.storagePublicationState).toLowerCase();
}

/**
 * Portal visibility for Storage publication.
 * - schema 1 / missing schema: documented legacy compatibility (missing state → ready)
 * - storageSchemaVersion >= 2: requires storagePublicationState === "ready"
 * Missing/pending/failed schema-2 is never portal-visible.
 */
export function isStorageReadyForPortal(file = {}) {
  if (!file || typeof file !== "object") return false;
  if (file.storageReady === false || file.storageOriginalVerified === false) return false;
  const state = normalizeStoragePublicationState(file);
  if (storageSchemaVersionOf(file) >= 2) {
    return state === STORAGE_PUBLICATION_STATES.READY;
  }
  if (!state) return true;
  return state === STORAGE_PUBLICATION_STATES.READY;
}

/**
 * Admin-only diagnostic for schema-2 records that are not portal-ready.
 * Does not change entitlement. Does not imply a production write.
 */
export function describeStoragePublicationAdminState(file = {}) {
  if (!file || typeof file !== "object") {
    return { key: "missing", label: "No file record", portalVisible: false, schema2: false };
  }
  const schema2 = storageSchemaVersionOf(file) >= 2;
  const state = normalizeStoragePublicationState(file);
  const portalVisible = isStorageReadyForPortal(file);
  if (!schema2) {
    return {
      key: state || "legacy_ready",
      label: state ? `Legacy storage (${state})` : "Legacy storage (compatible)",
      portalVisible,
      schema2: false,
    };
  }
  if (state === STORAGE_PUBLICATION_STATES.READY && portalVisible) {
    return { key: "ready", label: "Published (schema 2)", portalVisible: true, schema2: true };
  }
  if (state === STORAGE_PUBLICATION_STATES.PENDING) {
    return { key: "pending", label: "Schema 2 pending — not visible in portals", portalVisible: false, schema2: true };
  }
  if (state === STORAGE_PUBLICATION_STATES.FAILED) {
    return { key: "failed", label: "Schema 2 failed — not visible in portals", portalVisible: false, schema2: true };
  }
  return {
    key: "schema2_unready",
    label: "Schema 2 not marked ready — not visible in portals",
    portalVisible: false,
    schema2: true,
  };
}

export function isVerifiedStorageObjectMetadata(meta) {
  if (!meta || typeof meta !== "object") return false;
  const size = Number(meta.size);
  const path = safeStr(meta.fullPath || meta.name);
  return Number.isFinite(size) && size > 0 && Boolean(path);
}

export function planOverallPackagePublication({
  mode = "create",
  fileId = "",
  projectId = "",
  demoteFileIds = [],
  shareRecords = [],
} = {}) {
  const id = fileIdOf(fileId);
  const pid = safeStr(projectId);
  const storagePath = buildVersionedDrawingOriginalPath(pid, id);
  return {
    mode: mode === "replace" ? "replace" : "create",
    fileId: id,
    projectId: pid,
    storagePath,
    persistTokenUrl: false,
    demoteFileIds: (Array.isArray(demoteFileIds) ? demoteFileIds : [])
      .map(fileIdOf)
      .filter((v) => v && v !== id),
    shareRecords: Array.isArray(shareRecords) ? shareRecords.filter((row) => row && row.shareId) : [],
  };
}

/**
 * Pending pmProjectFiles row before versioned Storage upload/verify.
 * Storage read uses storageAdminAuthUids on this doc (not users/{uid} isAdmin).
 */
export function buildVersionedDrawingUploadStagingPayload({
  projectId,
  fileId,
  uid,
  includeThumbnailPath = true,
  existingFile = null,
} = {}) {
  const secureFields = buildSecureDrawingStorageWriteFields({
    projectId,
    fileId,
    includeThumbnailPath,
    actorUid: uid,
    existingFile,
    preserveViewAuthUids: Boolean(existingFile),
  });
  if (!secureFields) return null;
  const thumbPath = safeStr(secureFields.thumbnailStoragePath);
  return {
    projectId,
    ...secureFields,
    ...(thumbPath ? { thumbnailPath: thumbPath } : {}),
    storagePublicationState: STORAGE_PUBLICATION_STATES.PENDING,
    storageOriginalVerified: false,
    storageReady: false,
    status: "active",
  };
}

export async function writeVersionedDrawingUploadStagingDoc({
  db,
  writeBatch,
  doc,
  serverTimestamp,
  projectId,
  fileId,
  uid,
  mode = "create",
  existingFile = null,
  includeThumbnailPath = true,
} = {}) {
  const staging = buildVersionedDrawingUploadStagingPayload({
    projectId,
    fileId,
    uid,
    includeThumbnailPath,
    existingFile,
  });
  if (!staging) throw new Error("versioned-staging-fields-missing");
  if (typeof writeBatch !== "function" || typeof doc !== "function") {
    throw new Error("writeVersionedDrawingUploadStagingDoc requires writeBatch and doc");
  }
  const id = fileIdOf(fileId);
  const refDoc = doc(db, "pmProjectFiles", id);
  const stamp = typeof serverTimestamp === "function" ? serverTimestamp() : null;
  const actor = safeStr(uid) || null;
  const batch = writeBatch(db);
  const payload = {
    ...staging,
    ...(stamp ? { updatedAt: stamp, updatedBy: actor } : {}),
  };
  if (mode === "replace") {
    batch.update(refDoc, payload);
  } else {
    batch.set(refDoc, {
      ...payload,
      ...(stamp ? { createdAt: stamp, createdBy: actor } : {}),
    });
  }
  await batch.commit();
  return staging;
}

export function shouldDeleteOrphanAfterVerifyFailure(error) {
  const code = classifyStorageSdkError(error);
  return (
    code !== STORAGE_ERROR_CODES.UNAUTHORIZED &&
    code !== STORAGE_ERROR_CODES.UNAUTHENTICATED &&
    code !== STORAGE_ERROR_CODES.OBJECT_NOT_FOUND
  );
}

/** Secure drawing library upload after Storage metadata verify (not overall-package flow). */
export function buildSecureDrawingLibraryUploadFields({
  projectId,
  fileId,
  uid,
  includeThumbnailPath = true,
  existingFile = null,
} = {}) {
  const secureFields = buildSecureDrawingStorageWriteFields({
    projectId,
    fileId,
    includeThumbnailPath,
    actorUid: uid,
    existingFile,
    preserveViewAuthUids: Boolean(existingFile),
  });
  if (!secureFields) return null;
  const thumbPath = safeStr(secureFields.thumbnailStoragePath);
  return {
    ...secureFields,
    ...(thumbPath ? { thumbnailPath: thumbPath } : {}),
    storagePublicationState: STORAGE_PUBLICATION_STATES.READY,
    storageOriginalVerified: true,
    storageReady: true,
  };
}

export function buildPublishedPackageFields({
  projectId,
  fileId,
  file,
  uid,
  mode = "create",
  includeThumbnailPath = true,
  existingFile = null,
} = {}) {
  const replaceFile = mode === "replace" && existingFile && typeof existingFile === "object" ? existingFile : null;
  const secureFields = buildSecureDrawingStorageWriteFields({
    projectId,
    fileId,
    includeThumbnailPath,
    actorUid: uid,
    existingFile: replaceFile,
    preserveViewAuthUids: mode === "replace",
  });
  const payload = {
    ...(secureFields || {}),
    name: file?.name || null,
    contentType: file?.type || file?.contentType || null,
    size: typeof file?.size === "number" ? file.size : null,
    url: null,
    thumbnailUrl: null,
    drawingDocumentRole: "overall_package",
    storagePublicationState: STORAGE_PUBLICATION_STATES.READY,
    storageOriginalVerified: true,
    storageReady: true,
    updatedBy: uid || null,
  };
  if (mode === "create") {
    payload.projectId = projectId;
    payload.libraryType = "drawing";
    payload.category = "drawings";
    payload.reviewStatus = "open";
    payload.createdBy = uid || null;
  }
  return payload;
}

export function classifyPublicationFailure({ stage = "", error = null } = {}) {
  const normalized = safeStr(stage).toLowerCase();
  const errorCode = classifyStorageSdkError(error);
  const published = false;
  if (normalized === "upload" || normalized === "staging") {
    return {
      stage: normalized === "staging" ? "staging" : "upload",
      published,
      orphan: false,
      errorCode,
      adminMessage: ADMIN_UPLOAD_FAILURE_COPY,
    };
  }
  if (normalized === "verify") {
    const orphan = shouldDeleteOrphanAfterVerifyFailure(error);
    return {
      stage: "verify",
      published,
      orphan,
      errorCode,
      adminMessage: ADMIN_UPLOAD_FAILURE_COPY,
    };
  }
  if (normalized === "share" || normalized === "firestore") {
    return {
      stage: normalized === "share" ? "share" : "firestore",
      published,
      orphan: true,
      errorCode,
      adminMessage: ADMIN_UPLOAD_FAILURE_COPY,
    };
  }
  if (normalized === "thumbnail") {
    return {
      stage: "thumbnail",
      published: true,
      orphan: false,
      errorCode,
      adminMessage: "",
    };
  }
  return {
    stage: normalized || "unknown",
    published,
    orphan: false,
    errorCode,
    adminMessage: ADMIN_UPLOAD_FAILURE_COPY,
  };
}

export function describeOrphanedUpload({ fileId = "", storagePath = "", stage = "" } = {}) {
  return {
    orphan: true,
    fileId: safeStr(fileId) || null,
    storagePath: safeStr(storagePath) || null,
    stage: safeStr(stage) || null,
    cleanup: false,
    action: "report_only",
  };
}

export async function commitPublishedOverallPackageWrites({
  db,
  writeBatch,
  doc,
  serverTimestamp,
  mode = "create",
  fileId = "",
  filePayload = {},
  demoteFileIds = [],
  shareRecords = [],
} = {}) {
  if (typeof writeBatch !== "function" || typeof doc !== "function") {
    throw new Error("commitPublishedOverallPackageWrites requires writeBatch and doc");
  }
  const id = fileIdOf(fileId);
  if (!id) throw new Error("commitPublishedOverallPackageWrites requires fileId");
  const batch = writeBatch(db);
  const stamp = typeof serverTimestamp === "function" ? serverTimestamp() : null;
  const fileRef = doc(db, "pmProjectFiles", id);
  const payload = { ...filePayload };
  if (stamp) payload.updatedAt = stamp;
  if (mode === "create") {
    if (stamp) payload.createdAt = stamp;
    batch.set(fileRef, payload);
  } else {
    if (stamp) payload.mainFileReplacedAt = stamp;
    batch.update(fileRef, payload);
  }
  (Array.isArray(demoteFileIds) ? demoteFileIds : []).forEach((rawId) => {
    const otherId = fileIdOf(rawId);
    if (!otherId || otherId === id) return;
    const demote = { drawingDocumentRole: "individual" };
    if (stamp) demote.updatedAt = stamp;
    batch.update(doc(db, "pmProjectFiles", otherId), demote);
  });
  (Array.isArray(shareRecords) ? shareRecords : []).forEach((row) => {
    const shareId = safeStr(row?.shareId);
    if (!shareId || !row?.data) return;
    batch.set(doc(db, "projectFileShares", shareId), row.data);
  });
  await batch.commit();
}

/**
 * Upload → metadata verify → Firestore batch. Does not publish on upload/verify/batch failure.
 * Thumbnail generation is not part of publication; missing thumbs must not block the original.
 */
export async function executeVerifiedOverallPackagePublication({
  storage,
  db,
  file,
  fileId,
  projectId,
  uid,
  mode = "create",
  demoteFileIds = [],
  shareRecords = [],
  existingFile = null,
  uploadBytes,
  getMetadata,
  ref,
  writeBatch,
  doc,
  serverTimestamp,
  displayTitle = "",
} = {}) {
  const plan = planOverallPackagePublication({
    mode,
    fileId,
    projectId,
    demoteFileIds,
    shareRecords,
  });
  if (!plan.fileId || !plan.storagePath) {
    return {
      ok: false,
      ...classifyPublicationFailure({ stage: "upload" }),
      fileId: plan.fileId,
      storagePath: plan.storagePath,
    };
  }

  try {
    await writeVersionedDrawingUploadStagingDoc({
      db,
      writeBatch,
      doc,
      serverTimestamp,
      projectId,
      fileId: plan.fileId,
      uid,
      mode: plan.mode,
      existingFile: plan.mode === "replace" ? existingFile : null,
      includeThumbnailPath: true,
    });
  } catch (error) {
    return {
      ok: false,
      fileId: plan.fileId,
      storagePath: plan.storagePath,
      ...classifyPublicationFailure({ stage: "staging", error }),
    };
  }

  try {
    await uploadBytes(ref(storage, plan.storagePath), file, {
      contentType: file?.type || undefined,
    });
  } catch (error) {
    return {
      ok: false,
      fileId: plan.fileId,
      storagePath: plan.storagePath,
      ...classifyPublicationFailure({ stage: "upload", error }),
    };
  }

  let meta = null;
  try {
    meta = await getMetadata(ref(storage, plan.storagePath));
    if (!isVerifiedStorageObjectMetadata(meta)) {
      throw new Error("unverified-object");
    }
  } catch (error) {
    return {
      ok: false,
      fileId: plan.fileId,
      storagePath: plan.storagePath,
      orphanReport: describeOrphanedUpload({
        fileId: plan.fileId,
        storagePath: plan.storagePath,
        stage: "verify",
      }),
      ...classifyPublicationFailure({ stage: "verify", error }),
    };
  }

  const fields = buildPublishedPackageFields({
    projectId,
    fileId: plan.fileId,
    file,
    uid,
    mode: plan.mode,
    includeThumbnailPath: true,
    existingFile: plan.mode === "replace" ? existingFile : null,
  });
  if (displayTitle) fields.displayTitle = displayTitle;
  if (plan.mode === "replace") {
    fields.mainFileReplacedByUid = uid || null;
  }

  try {
    await commitPublishedOverallPackageWrites({
      db,
      writeBatch,
      doc,
      serverTimestamp,
      mode: plan.mode,
      fileId: plan.fileId,
      filePayload: fields,
      demoteFileIds: plan.demoteFileIds,
      shareRecords: plan.shareRecords,
    });
  } catch (error) {
    const stage = plan.shareRecords.length ? "share" : "firestore";
    return {
      ok: false,
      fileId: plan.fileId,
      storagePath: plan.storagePath,
      orphanReport: describeOrphanedUpload({
        fileId: plan.fileId,
        storagePath: plan.storagePath,
        stage,
      }),
      ...classifyPublicationFailure({ stage, error }),
    };
  }

  return {
    ok: true,
    published: true,
    fileId: plan.fileId,
    storagePath: plan.storagePath,
    persistTokenUrl: false,
  };
}

export const VERIFY_EXISTING_ORIGINAL_FAILURE_COPY =
  "The stored original could not be verified. This file was not marked ready.";

export function buildExistingOriginalReadyFields({ uid, existingFile = null } = {}) {
  const payload = {
    storagePublicationState: STORAGE_PUBLICATION_STATES.READY,
    storageOriginalVerified: true,
    storageReady: true,
    updatedBy: uid || null,
  };
  if (uid) {
    payload.storageAdminAuthUids = buildStorageAdminAuthUids({
      file: existingFile,
      actorUid: uid,
    });
  }
  return payload;
}

/**
 * Admin-only: verify the exact existing original object, then mark THIS fileId ready.
 * Does not allocate a new fileId, upload, copy shares, or persist token URLs.
 * Fail-closed: missing/unverified metadata never writes ready.
 */
export async function executeVerifyAndPublishExistingOriginal({
  storage,
  db,
  fileId,
  projectId,
  uid,
  existingFile = {},
  getMetadata,
  ref,
  writeBatch,
  doc,
  serverTimestamp,
} = {}) {
  const id = fileIdOf(fileId || existingFile);
  const pid = safeStr(projectId || existingFile?.projectId);
  const storagePath =
    resolveProjectFileSecureObjectPath({ ...existingFile, id, projectId: pid }, { kind: "original" }) ||
    buildVersionedDrawingOriginalPath(pid, id);
  if (!id || !storagePath || typeof getMetadata !== "function" || typeof ref !== "function") {
    return {
      ok: false,
      published: false,
      fileId: id,
      storagePath,
      stage: "verify",
      adminMessage: VERIFY_EXISTING_ORIGINAL_FAILURE_COPY,
    };
  }

  try {
    const meta = await getMetadata(ref(storage, storagePath));
    if (!isVerifiedStorageObjectMetadata(meta)) {
      throw new Error("unverified-object");
    }
  } catch (error) {
    return {
      ok: false,
      published: false,
      fileId: id,
      storagePath,
      ...classifyPublicationFailure({ stage: "verify", error }),
      adminMessage: VERIFY_EXISTING_ORIGINAL_FAILURE_COPY,
    };
  }

  if (typeof writeBatch !== "function" || typeof doc !== "function") {
    return {
      ok: false,
      published: false,
      fileId: id,
      storagePath,
      stage: "firestore",
      adminMessage: VERIFY_EXISTING_ORIGINAL_FAILURE_COPY,
    };
  }

  try {
    const batch = writeBatch(db);
    const stamp = typeof serverTimestamp === "function" ? serverTimestamp() : null;
    const payload = buildExistingOriginalReadyFields({ uid, existingFile });
    if (stamp) payload.updatedAt = stamp;
    batch.update(doc(db, "pmProjectFiles", id), payload);
    await batch.commit();
  } catch (error) {
    return {
      ok: false,
      published: false,
      fileId: id,
      storagePath,
      ...classifyPublicationFailure({ stage: "firestore", error }),
      adminMessage: VERIFY_EXISTING_ORIGINAL_FAILURE_COPY,
    };
  }

  return {
    ok: true,
    published: true,
    fileId: id,
    storagePath,
    persistTokenUrl: false,
  };
}

/**
 * Drawing library upload: staging Firestore doc (storageAdminAuthUids) → upload → verify → ready fields.
 */
export async function executeVerifiedSecureDrawingLibraryUpload({
  storage,
  db,
  file,
  fileId,
  projectId,
  uid,
  existingFile = null,
  uploadBytes,
  getMetadata,
  deleteObject,
  ref,
  writeBatch,
  doc,
  updateDoc,
  serverTimestamp,
  extraFirestoreFields = {},
} = {}) {
  const id = fileIdOf(fileId);
  const mode = existingFile?.id ? "replace" : "create";
  const storagePath = buildVersionedDrawingOriginalPath(projectId, id);
  if (!id || !storagePath) {
    return { ok: false, ...classifyPublicationFailure({ stage: "upload" }), fileId: id, storagePath };
  }

  try {
    await writeVersionedDrawingUploadStagingDoc({
      db,
      writeBatch,
      doc,
      serverTimestamp,
      projectId,
      fileId: id,
      uid,
      mode,
      existingFile,
      includeThumbnailPath: true,
    });
  } catch (error) {
    return {
      ok: false,
      fileId: id,
      storagePath,
      ...classifyPublicationFailure({ stage: "staging", error }),
    };
  }

  const storageRef = ref(storage, storagePath);
  try {
    await uploadBytes(storageRef, file, { contentType: file?.type || undefined });
  } catch (error) {
    return {
      ok: false,
      fileId: id,
      storagePath,
      ...classifyPublicationFailure({ stage: "upload", error }),
    };
  }

  try {
    const meta = await getMetadata(storageRef);
    if (!isVerifiedStorageObjectMetadata(meta)) {
      throw new Error("unverified-object");
    }
  } catch (error) {
    if (typeof deleteObject === "function" && shouldDeleteOrphanAfterVerifyFailure(error)) {
      try {
        await deleteObject(storageRef);
      } catch {
        /* ignore */
      }
    }
    return {
      ok: false,
      fileId: id,
      storagePath,
      orphanReport: describeOrphanedUpload({ fileId: id, storagePath, stage: "verify" }),
      ...classifyPublicationFailure({ stage: "verify", error }),
    };
  }

  const readyFields = buildSecureDrawingLibraryUploadFields({
    projectId,
    fileId: id,
    uid,
    includeThumbnailPath: true,
    existingFile,
  });
  const stamp = typeof serverTimestamp === "function" ? serverTimestamp() : null;
  const payload = {
    ...readyFields,
    ...extraFirestoreFields,
    ...(stamp ? { updatedAt: stamp, updatedBy: uid || null } : {}),
  };

  try {
    if (typeof updateDoc === "function") {
      await updateDoc(doc(db, "pmProjectFiles", id), payload);
    } else {
      const batch = writeBatch(db);
      batch.update(doc(db, "pmProjectFiles", id), payload);
      await batch.commit();
    }
  } catch (error) {
    return {
      ok: false,
      fileId: id,
      storagePath,
      ...classifyPublicationFailure({ stage: "firestore", error }),
    };
  }

  return { ok: true, fileId: id, storagePath, published: true };
}
